import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        success: 'bg-success text-success-foreground hover:bg-success/90',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 px-6',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /**
   * The click is in flight. Prepends a spinner, disables the button, and sets
   * `aria-busy`.
   *
   * This exists because `disabled` alone is not feedback. Every mutation in the
   * app already tracks a pending flag and every busy button already went
   * `disabled` — but a greyed button with unchanged text reads as *broken*, not
   * as working, which is the whole "clicks get stuck and it's weird" complaint.
   * A spinner is the difference between "the app is doing this" and "the app
   * ate my click".
   *
   * Children are kept rather than replaced, so the button does not change width
   * mid-click and shift everything beside it. Pass a present-tense label
   * (`Claiming…`) when the wait is long enough to want narrating.
   */
  loading?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    // `asChild` renders through Radix's Slot, which requires exactly ONE child.
    // This has to be a separate return rather than a conditional inside the JSX:
    // `{cond && <Loader2/>}{children}` passes React a two-element array even when
    // `cond` is false — the `false` counts — and Slot throws "failed to slot onto
    // its children". So asChild never takes the spinner branch at all; a caller
    // needing both a custom element and a spinner renders its own.
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      )
    }
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && <Loader2 className="animate-spin" aria-hidden="true" />}
        {children}
      </button>
    )
  },
)
Button.displayName = 'Button'

export { buttonVariants }
